import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const QUESTIONS = [
  { q: 'What do you need?', key: 'service', options: [
    { label: '🌐 Website', value: 'website' }, { label: '📱 Mobile App', value: 'app' },
    { label: '🔍 SEO', value: 'seo' }, { label: '📣 Digital Marketing', value: 'marketing' },
    { label: '🤖 AI Tool', value: 'ai' }, { label: '🎨 Design Only', value: 'design' },
  ] },
  { q: 'What is your business size?', key: 'size', options: [
    { label: '👤 Solo / Startup', value: 'solo' }, { label: '🏢 Small Business', value: 'small' },
    { label: '🏬 Mid-size Company', value: 'mid' }, { label: '🏭 Enterprise', value: 'enterprise' },
  ] },
  { q: 'What is your timeline?', key: 'timeline', options: [
    { label: '⚡ ASAP (1–2 weeks)', value: 'asap' }, { label: '📅 1 Month', value: 'month' },
    { label: '🗓️ 2–3 Months', value: 'quarter' }, { label: '🕐 Flexible', value: 'flexible' },
  ] },
];

const PRICES = {
  website:   { solo: '₹8K–₹20K',  small: '₹20K–₹50K', mid: '₹50K–₹1.5L', enterprise: '₹1.5L+' },
  app:       { solo: '₹25K–₹60K', small: '₹60K–₹1.5L', mid: '₹1.5L–₹4L', enterprise: '₹4L+' },
  seo:       { solo: '₹5K/mo',    small: '₹10K/mo',   mid: '₹20K/mo',   enterprise: '₹40K+/mo' },
  marketing: { solo: '₹8K/mo',    small: '₹15K/mo',   mid: '₹30K/mo',   enterprise: '₹60K+/mo' },
  ai:        { solo: '₹20K–₹60K', small: '₹60K–₹1.5L', mid: '₹1.5L–₹3L', enterprise: '₹3L+' },
  design:    { solo: '₹5K–₹15K',  small: '₹15K–₹40K',  mid: '₹40K–₹1L',  enterprise: '₹1L+' },
};

const TIMELINES = {
  website:   { asap: '1–2 wks', month: '2–4 wks', quarter: '1–2 mo', flexible: '2–4 wks' },
  app:       { asap: '3–4 wks', month: '4–6 wks', quarter: '2–3 mo', flexible: '4–8 wks' },
  seo:       { asap: 'Ongoing', month: 'Ongoing', quarter: 'Ongoing', flexible: 'Ongoing' },
  marketing: { asap: 'Week 1',  month: 'Week 1',  quarter: 'Week 1',  flexible: 'Week 1' },
  ai:        { asap: '2–4 wks', month: '4–8 wks', quarter: '2–3 mo', flexible: '4–8 wks' },
  design:    { asap: '1 wk',    month: '1–2 wks', quarter: '2–4 wks', flexible: '1–3 wks' },
};

function getEstimate(ans) {
  return {
    price: PRICES[ans.service]?.[ans.size] || '₹10K–₹50K',
    time: TIMELINES[ans.service]?.[ans.timeline] || '2–4 weeks',
  };
}

// onClose: optional callback passed by the parent (e.g. Navbar) that closes
// the popup wrapping this component. Safe to omit — component still works
// standalone if no onClose is passed.
export default function AIProjectConsultant({ onClose }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const handleOption = (key, value) => {
    const newAnswers = { ...answers, [key]: value };
    setAnswers(newAnswers);
    if (step < QUESTIONS.length - 1) setStep((s) => s + 1);
    else setResult(getEstimate(newAnswers));
  };

  const reset = () => { setStep(0); setAnswers({}); setResult(null); };

  const goToContact = () => {
    if (onClose) onClose();
    navigate('/contact');
  };

  return (
    <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl overflow-hidden w-full flex flex-col">

      {/* Header */}
      <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-black">
              <path d="M12 2a10 10 0 110 20A10 10 0 0112 2z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M8 12s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <p className="text-white text-xs font-medium">AI Project Consultant</p>
            <p className="text-white/30 text-[11px]">Get instant quote</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-white/30 text-[11px]">Live</span>
        </div>
      </div>

      {/* Progress bar */}
      {!result && (
        <div className="h-0.5 bg-white/5">
          <div
            className="h-full bg-white transition-all duration-500"
            style={{ width: `${(step / QUESTIONS.length) * 100}%` }}
          />
        </div>
      )}

      {/* Body */}
      <div className="p-5 flex-1 flex flex-col justify-between min-h-[300px]">
        {!result ? (
          <>
            {/* Step dots */}
            <div className="flex items-center gap-2 mb-4">
              {QUESTIONS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                    i <= step ? 'bg-white' : 'bg-white/15'
                  }`}
                />
              ))}
            </div>

            {/* Question */}
            <div className="mb-4">
              <p className="text-white/40 text-[11px] mb-1">
                Step {step + 1} of {QUESTIONS.length}
              </p>
              <p className="text-white font-medium text-sm">{QUESTIONS[step].q}</p>
            </div>

            {/* Options */}
            <div className="grid grid-cols-2 gap-2 flex-1">
              {QUESTIONS[step].options.map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => handleOption(QUESTIONS[step].key, value)}
                  className="bg-white/5 border border-white/10 hover:border-white hover:bg-white/10 rounded-xl px-3 py-3 text-xs text-white/70 hover:text-white transition-all duration-200 text-left font-medium"
                >
                  {label}
                </button>
              ))}
            </div>
          </>
        ) : (
          /* Result */
          <div className="flex flex-col items-center justify-center text-center flex-1 gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-black">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            </div>
            <div>
              <p className="text-white/40 text-xs mb-1">Estimated Investment</p>
              <p
                className="text-3xl text-white font-black mb-1"
                style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
              >
                {result.price}
              </p>
              <p className="text-white/40 text-xs">Timeline: {result.time}</p>
            </div>
            <div className="w-full flex flex-col gap-2 mt-2">
              <button
                onClick={goToContact}
                className="w-full bg-white text-black text-xs font-medium py-3 rounded-xl hover:bg-white/90 transition-colors"
              >
                Get Exact Quote →
              </button>
              <button
                onClick={reset}
                className="text-white/30 text-xs hover:text-white transition-colors"
              >
                Start over
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
